import { useState, useEffect, useCallback, useRef } from 'react';
import { useOfflineCache } from './useOfflineCache';
import { useAuth } from './useAuth';

/**
 * Offline-Aware Data Hook
 * 
 * Provides seamless data access that:
 * - Tries network first when online
 * - Falls back to cache when offline
 * - Automatically syncs when back online
 * - Provides loading states for both scenarios
 */

export const useOfflineAwareData = () => {
  const { userId, isLoading: authLoading } = useAuth();
  const {
    isOnline,
    cacheStats,
    cacheOrders,
    cacheJobs,
    cacheResults,
    getCachedOrders,
    getCachedJobs,
    getCachedResults,
    queueForSync,
    isInitialized: cacheInitialized
  } = useOfflineCache(userId);

  const [dataState, setDataState] = useState({
    orders: [],
    jobs: {},
    results: {},
    loading: false,
    error: null,
    source: null, // 'network', 'cache', or 'hybrid'
    lastFetch: null
  });

  const pendingOperationsRef = useRef(new Set());

  // Enhanced loading with network/cache awareness
  const loadOrdersWithFallback = useCallback(async (
    networkFetcher,
    filters = {},
    options = {}
  ) => {
    // If cache not initialized, just use network fetcher directly
    if (!cacheInitialized) {
      console.log('📡 Cache not ready, using direct network call');
      return await networkFetcher();
    }
    const { 
      forceNetwork = false, 
      cacheFirst = false,
      operationId = `orders_${Date.now()}`
    } = options;

    // Prevent duplicate operations
    if (pendingOperationsRef.current.has(operationId)) {
      return dataState.orders;
    }
    pendingOperationsRef.current.add(operationId);

    try {
      setDataState(prev => ({ 
        ...prev, 
        loading: true, 
        error: null 
      }));

      let orders = [];
      let source = 'network';

      // Strategy 1: Cache-first (for better perceived performance)
      if (cacheFirst && cacheInitialized && !forceNetwork) {
        console.log('📱 Loading orders from cache first...');
        const cachedOrders = await getCachedOrders(filters);
        
        if (cachedOrders.length > 0) {
          orders = cachedOrders;
          source = 'cache';
          
          setDataState(prev => ({
            ...prev,
            orders: cachedOrders,
            source: 'cache',
            loading: false,
            lastFetch: Date.now()
          }));

          // Background network update if online
          if (isOnline) {
            queueForSync(async () => {
              try {
                const networkOrders = await networkFetcher();
                await cacheOrders(networkOrders);
                
                setDataState(prev => ({
                  ...prev,
                  orders: networkOrders,
                  source: 'hybrid',
                  lastFetch: Date.now()
                }));
                
                console.log('🔄 Background sync completed for orders');
              } catch (error) {
                console.warn('Background sync failed:', error);
              }
            });
          }
          
          return cachedOrders;
        }
      }

      // Strategy 2: Network-first (default)
      if (isOnline && !cacheFirst) {
        try {
          console.log('🌐 Loading orders from network...');
          orders = await networkFetcher();
          source = 'network';
          
          // Cache successful network response
          if (cacheInitialized && orders.length > 0) {
            await cacheOrders(orders);
          }
        } catch (networkError) {
          console.warn('Network fetch failed, trying cache:', networkError);
          
          if (cacheInitialized) {
            orders = await getCachedOrders(filters);
            source = orders.length > 0 ? 'cache' : 'network';
            
            if (orders.length === 0) {
              throw networkError; // Re-throw if no cache available
            }
          } else {
            throw networkError;
          }
        }
      }

      // Strategy 3: Offline-only
      if (!isOnline && cacheInitialized) {
        console.log('📴 Loading orders from cache (offline)...');
        orders = await getCachedOrders(filters);
        source = 'cache';
        
        if (orders.length === 0) {
          throw new Error('No cached data available offline');
        }
      }

      setDataState(prev => ({
        ...prev,
        orders,
        source,
        loading: false,
        error: null,
        lastFetch: Date.now()
      }));

      return orders;

    } catch (error) {
      console.error('Failed to load orders:', error);
      
      setDataState(prev => ({
        ...prev,
        loading: false,
        error: error.message,
        source: null
      }));
      
      throw error;
    } finally {
      pendingOperationsRef.current.delete(operationId);
    }
  }, [
    isOnline,
    cacheInitialized,
    getCachedOrders,
    cacheOrders,
    queueForSync,
    dataState.orders
  ]);

  const loadJobsWithFallback = useCallback(async (
    networkFetcher,
    orderIds,
    options = {}
  ) => {
    const { 
      forceNetwork = false,
      cacheFirst = false,
      operationId = `jobs_${orderIds.join('_')}_${Date.now()}`
    } = options;

    if (pendingOperationsRef.current.has(operationId)) {
      return dataState.jobs;
    }
    pendingOperationsRef.current.add(operationId);

    try {
      let jobs = [];
      let source = 'network';

      // Cache-first strategy
      if (cacheFirst && cacheInitialized && !forceNetwork) {
        const cachedJobs = await getCachedJobs(orderIds);
        
        if (cachedJobs.length > 0) {
          jobs = cachedJobs;
          source = 'cache';
          
          const jobsByOrderId = jobs.reduce((acc, job) => {
            if (!acc[job.orderId]) acc[job.orderId] = [];
            acc[job.orderId].push(job);
            return acc;
          }, {});

          setDataState(prev => ({
            ...prev,
            jobs: { ...prev.jobs, ...jobsByOrderId },
            source: 'cache'
          }));

          // Background sync if online
          if (isOnline) {
            queueForSync(async () => {
              try {
                const networkJobs = await networkFetcher();
                await cacheJobs(networkJobs);
                
                const networkJobsByOrderId = networkJobs.reduce((acc, job) => {
                  if (!acc[job.orderId]) acc[job.orderId] = [];
                  acc[job.orderId].push(job);
                  return acc;
                }, {});

                setDataState(prev => ({
                  ...prev,
                  jobs: { ...prev.jobs, ...networkJobsByOrderId },
                  source: 'hybrid'
                }));
              } catch (error) {
                console.warn('Background job sync failed:', error);
              }
            });
          }
          
          return jobsByOrderId;
        }
      }

      // Network-first strategy
      if (isOnline && !cacheFirst) {
        try {
          jobs = await networkFetcher();
          source = 'network';
          
          if (cacheInitialized && jobs.length > 0) {
            await cacheJobs(jobs);
          }
        } catch (networkError) {
          if (cacheInitialized) {
            jobs = await getCachedJobs(orderIds);
            source = jobs.length > 0 ? 'cache' : 'network';
            
            if (jobs.length === 0) {
              throw networkError;
            }
          } else {
            throw networkError;
          }
        }
      }

      // Offline-only
      if (!isOnline && cacheInitialized) {
        jobs = await getCachedJobs(orderIds);
        source = 'cache';
      }

      const jobsByOrderId = jobs.reduce((acc, job) => {
        if (!acc[job.orderId]) acc[job.orderId] = [];
        acc[job.orderId].push(job);
        return acc;
      }, {});

      setDataState(prev => ({
        ...prev,
        jobs: { ...prev.jobs, ...jobsByOrderId },
        source
      }));

      return jobsByOrderId;

    } catch (error) {
      console.error('Failed to load jobs:', error);
      throw error;
    } finally {
      pendingOperationsRef.current.delete(operationId);
    }
  }, [
    isOnline,
    cacheInitialized,
    getCachedJobs,
    cacheJobs,
    queueForSync,
    dataState.jobs
  ]);

  const loadResultsWithFallback = useCallback(async (
    networkFetcher,
    jobIds,
    options = {}
  ) => {
    const { 
      forceNetwork = false,
      cacheFirst = true, // Results benefit more from cache-first
      operationId = `results_${jobIds.join('_')}_${Date.now()}`
    } = options;

    if (pendingOperationsRef.current.has(operationId)) {
      return dataState.results;
    }
    pendingOperationsRef.current.add(operationId);

    try {
      let results = {};
      let source = 'network';

      // Cache-first (results change less frequently)
      if (cacheFirst && cacheInitialized && !forceNetwork) {
        const cachedResults = await getCachedResults(jobIds);
        
        if (Object.keys(cachedResults).length > 0) {
          results = cachedResults;
          source = 'cache';
          
          setDataState(prev => ({
            ...prev,
            results: { ...prev.results, ...cachedResults },
            source: 'cache'
          }));

          // Background sync for missing results
          const missingJobIds = jobIds.filter(id => !cachedResults[id]);
          if (isOnline && missingJobIds.length > 0) {
            queueForSync(async () => {
              try {
                const networkResults = await networkFetcher(missingJobIds);
                await cacheResults(networkResults);
                
                setDataState(prev => ({
                  ...prev,
                  results: { ...prev.results, ...networkResults },
                  source: 'hybrid'
                }));
              } catch (error) {
                console.warn('Background results sync failed:', error);
              }
            });
          }
          
          return cachedResults;
        }
      }

      // Network-first
      if (isOnline && !cacheFirst) {
        try {
          results = await networkFetcher(jobIds);
          source = 'network';
          
          if (cacheInitialized && Object.keys(results).length > 0) {
            await cacheResults(results);
          }
        } catch (networkError) {
          if (cacheInitialized) {
            results = await getCachedResults(jobIds);
            source = Object.keys(results).length > 0 ? 'cache' : 'network';
            
            if (Object.keys(results).length === 0) {
              throw networkError;
            }
          } else {
            throw networkError;
          }
        }
      }

      // Offline-only
      if (!isOnline && cacheInitialized) {
        results = await getCachedResults(jobIds);
        source = 'cache';
      }

      setDataState(prev => ({
        ...prev,
        results: { ...prev.results, ...results },
        source
      }));

      return results;

    } catch (error) {
      console.error('Failed to load results:', error);
      throw error;
    } finally {
      pendingOperationsRef.current.delete(operationId);
    }
  }, [
    isOnline,
    cacheInitialized,
    getCachedResults,
    cacheResults,
    queueForSync,
    dataState.results
  ]);

  // Clear data state when user changes
  useEffect(() => {
    if (userId) {
      setDataState({
        orders: [],
        jobs: {},
        results: {},
        loading: false,
        error: null,
        source: null,
        lastFetch: null
      });
    }
  }, [userId]);

  return {
    // Data state
    ...dataState,
    
    // Network status
    isOnline,
    cacheStats,
    
    // Loading functions with fallback
    loadOrdersWithFallback,
    loadJobsWithFallback,
    loadResultsWithFallback,
    
    // Utilities
    isReady: !authLoading && cacheInitialized,
    
    // Cache management
    queueForSync,
    
    // Debug info
    _debug: {
      authLoading,
      cacheInitialized,
      isReady: !authLoading && cacheInitialized
    }
  };
};
